import { z } from 'zod';
import { distance } from 'fastest-levenshtein';

import { CapicheLoggerCore } from '@capiche/ts-logger-core';

import { zodEditStateEnum, editStateEnum, zodSpellingStateEnum, spellingStateEnum } from './data-model.enums';
import { suggestionSchema } from './data-model.suggestion';
import { normalOf, keyOf } from './util-text-normaliser';

const logger: CapicheLoggerCore = new CapicheLoggerCore('token:', false);

// the token object schema
//  each token represents a word in the input string
//  each token has an index and a length which represent the position of the word in the input string including any/all punctuation etc.
export const tokenSchema = z.strictObject({
  // the tokenized string exactly as provided by the user input (mixed case, digits, accents, symbols etc.) @mention etc. but without surrounding punctuation
  input: z.string().min(1), // required().min(1), // exactly as entered by the user including case, punctuation, digits, accents, symbols etc. for calculating replacement indexes etc.
  normal: z.string(), //.min(1), // raw > normalised (e.g., lowercased, diacritics removed, standard apostrophes/hyphens characters etc.) for comparison purposes
  key: z.string(), //.min(1), // normal > drop any chars not in a-z

  // should input really be more like this..
  // input: z.string().min(1), // the tokenized string  input string in lowercase, punctuation free(except apostrophe).
  // raw: z.string().min(1).optional(), // the tokenized string exactly as provided by the user input
  // normalised: z.string().min(1).optional(), // the normalised form of the input string (e.g., lowercased, diacritics removed) for comparison purposes

  // the index of the token in the input string
  //        index: Joi.number().required().integer().min(0),
  index: z.number().int().min(0),
  // the length of the token in the input string
  //        length: Joi.number().required().integer().min(1),
  length: z.number().int().min(1),
  // the state of the token in the editor
  // this state is used by the client to control the display of suggestions
  //  for example words that have been replaced by the user should not show suggestions
  // this state is used by the server to control the processing of the token
  //  for example words that have been replaced by the user can be given more weight when
  //   assessing context to determine the best suggestions for adjacent/other words in the input string
  editState: zodEditStateEnum,
  // the spelling state of the token
  // this state is used by the client to control the display of errors
  //  for example tokens that are unknown (incorrectly spelled - and have suggestsion) can be highlighted in red
  //  for example tokens that are unknown (incorrectly spelled - and have no suggestions) should not highlught
  //   and tokens that contextual (are known but are probably incorrect in context) can be highlighted in yellow
  spellingState: zodSpellingStateEnum,
  // the array of spelling suggestions for the token
  //  in case where the editState of the token is checked the array must contain zero or more suggestions
  //   an arry of zero elements indicates that the word not known and should therefore also have spellingStateEnum.unknown
  //  TODO: it is ideal that the spelling state be spellingStateEnum.correct but spellingStateEnum.contextual is possible (in the case of the user selecting the wrong replacement word)
  //  this is provided by the server to the client in response to calls to the getSuggestions endpoint
  //  in cases where there are no suggestions the array will be empty
  //  in cases where there are suggestions the array will contain one or more suggestions
  suggestions: z.array(suggestionSchema).optional(),
});

export type tokenDTO = z.infer<typeof tokenSchema>;

export class Token {
  constructor(public readonly dto: tokenDTO) {
    logger.log('constructor:', dto);
  }

  // Factory methods
  static fromDTO(dto: tokenDTO): Token {
    tokenSchema.parse(dto);
    return new Token(dto);
  }

  static fromString(
    text: string,
    index: number = 0,
    spellingState: spellingStateEnum = spellingStateEnum.unknown,
  ): Token {
    return new Token({
      input: text,
      normal: normalOf(text),
      key: keyOf(text),
      index,
      length: text.length,
      editState: editStateEnum.new,
      spellingState,
    });
  }

  // Serialization
  toDTO(): tokenDTO {
    return this.dto;
  }

  // Business logic
  isMisspelled(): boolean {
    return this.dto.spellingState === spellingStateEnum.misspelled;
  }

  // compare raw input (case-insensitive, accents preserved)
  inputMatches(other: string | Token, caseSensitive: boolean = false): boolean {
    const t: string = this.dto.input;
    const o: string = typeof other === 'string' ? other : other.dto.input;
    return caseSensitive ? t === o : t.toLowerCase() === o.toLowerCase();
  }

  // compare normalised form (case-folded, accents removed, apostrophes/hyphens/quotes normalised)
  normalMatches(other: string | Token, caseSensitive: boolean = false): boolean {
    const t: string = this.dto.normal;
    const o: string = typeof other === 'string' ? normalOf(other) : other.dto.normal;
    return caseSensitive ? t === o : t.toLowerCase() === o.toLowerCase();
  }

  // compare dictionary key (strict a–z)
  keyMatches(other: string | Token): boolean {
    const t: string = this.dto.key;
    const o: string = typeof other === 'string' ? keyOf(other) : other.dto.key;
    return t === o;
  }

  hasSuggestion(query: string): boolean {
    return this.dto.suggestions?.some((s) => s.word.toLowerCase() === query.toLowerCase()) ?? false;
  }

  hasSuggestions(): boolean {
    return (this.dto.suggestions?.length ?? 0) > 0;
  }

  distanceTo(other: Token): number {
    const t: string = this.dto.input.toLowerCase();
    const o: string = other.dto.input.toLowerCase();

    return distance(t, o);
  }

  similarityTo(other: Token): number {
    const t: string = this.dto.input.toLowerCase();
    const o: string = other.dto.input.toLowerCase();

    const distance: number = this.distanceTo(other);
    return 1 - distance / Math.max(t.length, o.length);
  }
}
